import React, { useEffect, useRef, useState } from 'react';
import Spline from '@splinetool/react-spline';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import gsap from 'gsap';

const MoleculeComponent = () => {
	const moleculeAppEl = document.getElementById('molecule-app');
	const initialTitle = moleculeAppEl?.dataset.title || '';
	const initialIntro = moleculeAppEl?.dataset.intro || '';

	const moleculeObj = useRef(null);
	const introRef = useRef(null);
	const contentContainerRef = useRef(null);
	const referencesContainerRef = useRef(null);
	
	const [showModal, setShowModal] = useState(true);
	const [showInstructionsReset, setShowInstructionsReset] = useState(false);
	const [showAnnotations, setShowAnnotations] = useState(false);
	const [showResetBtn, setShowResetBtn] = useState(false);
	const [showIntroModal, setShowIntroModal] = useState(true);
	const [showExtraModal, setShowExtraModal] = useState(false);
	const [initSpline, setInitSpline] = useState(false);
	const [title, setTitle] = useState('');
	const [currentColor, setCurrentColor] = useState('');
	const [content, setContent] = useState('');
	const [modalButton, setModalButton] = useState('');
	const [extraModal, setExtraModal] = useState('');
	const [references, setReferences] = useState([]);
	const [showReferences, setShowReferences] = useState(false);
	const [showLottieGraph, setShowLottieGraph] = useState(false);

	useEffect(() => {
		if (referencesContainerRef.current) {
			gsap.set(referencesContainerRef.current.parentElement, { yPercent: 100, opacity: 0 });
		}
		if (introRef.current) {
			gsap.set([introRef.current, document.getElementById('footer-container'), document.getElementById('offcanvas-toggler')], { opacity: 0 });
		}
	}, []);

	useEffect(() => {
		window.handleModalButtonClick = () => {
			setShowExtraModal(true);
			setShowModal(false);
		};
	}, []);

	const modalButtonHTML = modalButton ? `<button id="modal-button" onClick="window.handleModalButtonClick()" class="glow  mt-8 px-4 py-2 cursor-pointer">${modalButton}</button>` : '';

	// Function to load default references
	const loadDefaultReferences = async () => {
		try {
			const response = await fetch('/wp-json/molecule-content/default-references');
			if (response.ok) {
				const data = await response.json();
				if (data?.references && data.references.length > 0) {
					setReferences(data.references);
					setShowReferences(false); // Reset to false so first click will show
					// Wait a bit for the footer to be visible before showing the button
					setTimeout(() => {
						const refBtn = document.getElementById('referencePaneToggle');
						if (refBtn) {
							refBtn.style.display = 'flex';
						} else {
							console.log('Reference button not found');
						}
					}, 100);
				} else {
					console.log('No default references found');
				}
			} else {
				console.log('Failed to load default references, status:', response.status);
			}
		} catch (error) {
			console.error('Error loading default references:', error);
		}
	};

	// Load default references when component mounts (only if footer is already visible)
	useEffect(() => {
		// Only load if the footer is already visible (for page refreshes in development)
		const footerContainer = document.getElementById('footer-container');
		if (footerContainer && window.getComputedStyle(footerContainer).opacity !== '0') {
			loadDefaultReferences();
		}
	}, []);

	const handleModalChoice = (choice) => {
		setShowModal(false);
		if (choice === 'yes') {
			setInitSpline(true);
			gsap.to([introRef.current, document.getElementById('footer-container'), document.getElementById('offcanvas-toggler')], {
				opacity: 1,
				duration: 0.5,
				ease: 'power1.inOut',
				onComplete: () => {
					// Load default references after the footer is visible
					loadDefaultReferences();
				}
			});
		} else {
			window.location.href = 'https://www.sobi.com/netherlands/nl';
		}
	};

	const toggleReferencesPane = () => {
		if (referencesContainerRef.current) {
			gsap.to(referencesContainerRef.current.parentElement, {
				opacity: showReferences ? 0 : 1,
				yPercent: showReferences ? 100 : 0,
				duration: 0.5,
				ease: 'power1.inOut',
			});
		}
		setShowReferences(!showReferences);
	};

	const referencePaneToggle = document.getElementById('referencePaneToggle');
	referencePaneToggle?.addEventListener('click', toggleReferencesPane);

	const zoomBG = (restore) => {
		const video = document.getElementById('bg-video');
		video.style.scale = restore ? '1.15' : '1';
	};

	const cleanDOM = (restore = false) => {
		const refBtn = document.getElementById('referencePaneToggle');
		if (refBtn) {
			refBtn.style.display = 'none';
		}
		setShowResetBtn(false);
		zoomBG(restore);
		if (introRef.current) {
			gsap.to(introRef.current, {
				opacity: restore ? 1 : 0,
				yPercent: restore ? 0 : -100,
				duration: 0.5,
				ease: 'power1.inOut',
			});
		}
		if (restore) {
			if (referencesContainerRef.current) {
				gsap.set(referencesContainerRef.current.parentElement, { yPercent: 100, opacity: 0 });
			}
			setShowReferences(false); // Reset references state
			// Load default references when restoring overview
			setTimeout(() => {
				loadDefaultReferences();
			}, 100);
			if (contentContainerRef.current) {
				gsap.to(contentContainerRef.current, {
					opacity: 0,
					duration: 0.5,
					ease: 'power1.inOut',
					onComplete: () => {
						setTitle('');
						setContent('');
						setCurrentColor('');
					}
				});
			}
		}
	};
	
	const handleClick = async (e) => {
		const color = e.target.name;
		if (color === 'bt-work' || color === 'bt-return') {
			zoomBG(false);
			return;
		}
		if (color.includes('bt-home')) {
			cleanDOM(true);
			return;
		}
		cleanDOM(false);
		try {
			const data = await getContent(color);
			if (data?.data) {
				setTitle('<h2 class="glow-heading">' + data.title + '</h2>');
				setContent(data.data);
				setModalButton(data.modal_button);
				setExtraModal(data.modal);
				setShowResetBtn(true);
				if (contentContainerRef.current) {
					gsap.to(contentContainerRef.current, {
						opacity: 1,
						duration: 0.5,
						ease: 'power1.inOut',
					});
				}
			}
			if (data?.references && data.references.length > 0) {
				const refBtn = document.getElementById('referencePaneToggle');
				setReferences(data.references);
				setShowReferences(false); // Reset to false so first click will show
				if (refBtn) {
					refBtn.style.display = 'flex';
				}
			}
		} catch (error) {
			console.error('Error fetching content:', error);
		}
	};

	const getContent = async (color) => {
		let colorName = color.split('-').pop();
		setCurrentColor(colorName);
		try {
			const response = await fetch(`/wp-json/molecule-content/${colorName}`);
			if (!response.ok) {
				return;
			}
			
			return await response.json();
		} catch (error) {
			console.error('Fetch error:', error);
		}
	};

	const initSplineModule = (splineObj) => {
		const loader = document.getElementById('splineLoader');
		const spline = document.getElementById('altuvoct-spline').querySelector('canvas');
		moleculeObj.current = splineObj;

		gsap.to(spline, {
			opacity: 1,
			delay: 2.0,
			duration: 0.8,
			ease: 'power1.inOut',
			onStart: () => {
				if (loader) {
					loader.style.display = 'none';
				}
			}
		});
	};

	const resetBtnAction = () => {
		moleculeObj.current.emitEvent('mouseDown', currentColor + '-bt-home');
	};

	const initInstructionsSpline = () => {
		moleculeObj.current.emitEvent('mouseDown', 'bt-work');
		setShowInstructionsReset(true);
		setShowAnnotations(true);
		setTimeout(() => {
			const annotationEl = document.getElementById('altuvoct-animation-annotations');
			if (annotationEl) {
				const items = annotationEl.querySelectorAll('ol > li');
				const tl = gsap.timeline();
				if (items[0]) tl.to(items[0], { opacity: 1, duration: 0.5 }, 0);
				if (items[1]) tl.to(items[1], { opacity: 1, duration: 0.5 }, 5);
				if (items[2]) tl.to(items[2], { opacity: 1, duration: 0.5 }, 15);
			}
		}, 100);
	}

	const resetInstructionsSpline = () => {
		moleculeObj.current.emitEvent('mouseDown', 'bt-return');
		const annotationEl = document.getElementById('altuvoct-animation-annotations');
		if (annotationEl) {
			const items = annotationEl.querySelectorAll('ol > li');
			gsap.set(items, { opacity: 0 });
		}
		setShowInstructionsReset(false);
		setShowAnnotations(false);
	}

	return (
    <div>
      {showModal && (
        <div className="modal absolute inset-0 z-[9999] flex items-center justify-center">
          <div className="absolute inset-0 bg-white/50"></div>
          <div className="modal-content z-[99999] flex max-h-screen flex-col items-center overflow-scroll rounded-2xl bg-white p-8 shadow-2xl xl:max-w-[500px]">
            <img
              src="/wp-content/themes/brut-communicatie-tw/assets/img/sobi-color.svg"
              alt="Sobi logo"
              className="mb-8 max-w-[200px]"
            />
            <p
              className="mb-6 font-bold"
              dangerouslySetInnerHTML={{
                __html: brut_variables.spline_init_modal_content,
              }}
            ></p>
            <div className="button-group flex justify-center gap-4 [&_button]:cursor-pointer [&_button]:bg-primary/40 [&_button]:px-4 [&_button]:py-2 [&_button]:text-white [&_button]:hover:bg-primary">
              <button onClick={() => handleModalChoice("yes")}>
                {brut_variables.translate_yes}
              </button>
              <button onClick={() => handleModalChoice("no")}>
                {brut_variables.translate_no}
              </button>
            </div>
          </div>
        </div>
      )}

      {showExtraModal && (
        <div className="modal absolute top-4 bottom-4 z-[99999] flex justify-center overflow-scroll border border-white bg-primary-light p-4 lg:top-16 lg:right-4 lg:bottom-16 lg:max-w-[50vw] lg:p-20 [&_li]:mb-4 [&_ul]:mt-8 [&_ul]:pl-4">
          <button
            className="absolute top-8 right-8 text-2xl text-white"
            onClick={() => setShowExtraModal(false)}
          >
            <i className="fa-solid fa-xmark"></i>
          </button>
          <div
            dangerouslySetInnerHTML={{ __html: extraModal }}
            className="text-white [&_p]:mb-4"
          ></div>
        </div>
      )}

      <div
        ref={introRef}
        id="altuvoct-intro"
        className="absolute top-8 left-8 z-[1000] w-96 max-w-screen text-white"
        style={{ display: showAnnotations ? "none" : "block" }}
      >
        <h1 className="mb-8 flex items-center gap-4 uppercase">
          <i
            className={`fa-solid flex! h-10 w-10 items-center justify-center ${showIntroModal ? "fa-xmark" : "fa-plus"} rounded-sm border border-white text-base text-white lg:hidden!`}
            onClick={() => setShowIntroModal(!showIntroModal)}
          ></i>
          <span
            className="pointer-events-none"
            dangerouslySetInnerHTML={{ __html: initialTitle }}
          ></span>
        </h1>
        <div
          className="hidden text-sm lg:text-base xl:inline-block [&_p]:mb-4"
          dangerouslySetInnerHTML={{ __html: initialIntro }}
        ></div>
        <div className="altuvoct-intro-buttons mt-4 flex flex-col gap-2">
          {!showInstructionsReset && (
            <a className="cursor-pointer" onClick={initInstructionsSpline}>
              <i className="fa-sharp fa-solid fa-circle-play text-white"></i>{" "}
              <span
                dangerouslySetInnerHTML={{
                  __html: brut_variables.translate_show_instruction_video,
                }}
              />
            </a>
          )}
        </div>
      </div>

      <div
        id="altuvoct-animation-annotations"
        className="absolute top-8 left-8 z-[1000] w-96 max-w-screen text-white"
        style={{ display: showAnnotations ? "block" : "none" }}
      >
        <h1 className="mb-8">
          ALTUVOCT<sup>®</sup>
        </h1>
        <h2 className="mb-4 text-2xl">
          <strong>Werkingsmechanisme</strong>
        </h2>
        <ol className="mb-8 list-decimal">
          {Object.entries(brut_variables.animation_annotation).map(
            ([key, value], idx) => (
              <li
                key={key}
                className="mb-4 ml-5 [&_strong]:block"
                style={{ opacity: 0 }}
                dangerouslySetInnerHTML={{ __html: value.text }}
              ></li>
            ),
          )}
        </ol>
        {showInstructionsReset && (
          <a className="cursor-pointer" onClick={resetInstructionsSpline}>
            <i className="fa-solid fa-rotate-left"></i>{" "}
            {brut_variables.translate_reset}
          </a>
        )}
      </div>

      {showIntroModal && (
        <div
          className="relative z-[999] flex h-screen w-screen items-center bg-black/40 lg:hidden"
          id="altuvoct-intro-mobile"
          onClick={() => setShowIntroModal(false)}
        >
          <div
            className="max-w-2/3 pl-22 text-sm text-white lg:text-base"
            dangerouslySetInnerHTML={{ __html: initialIntro }}
          ></div>
          <div className="altuvoct-intro-buttons mt-4 flex flex-col gap-2">
            {!showInstructionsReset && (
              <a className="cursor-pointer" onClick={initInstructionsSpline}>
                <i className="fa-sharp fa-solid fa-circle-play text-white"></i>{" "}
                <span
                  dangerouslySetInnerHTML={{
                    __html: brut_variables.translate_show_instruction_video,
                  }}
                />
              </a>
            )}
            {showInstructionsReset && (
              <a className="cursor-pointer" onClick={resetInstructionsSpline}>
                <i className="fa-solid fa-rotate-left"></i>{" "}
                {brut_variables.translate_reset}
              </a>
            )}
          </div>
        </div>
      )}

      {showResetBtn && (
        <div
          className="reset-button absolute top-8 left-8 z-[999] max-w-20 cursor-pointer lg:max-w-44"
          onClick={resetBtnAction}
        >
          <img
            src={`/wp-content/themes/brut-communicatie-tw/assets/img/${currentColor}-bt-home.png`}
            alt=""
          />
          <span
            className="mt-4 block mx-auto w-fit cursor-pointer rounded-full bg-primary px-8 py-4 hover:bg-white hover:text-primary text-white text-center"
            dangerouslySetInnerHTML={{
              __html: brut_variables.translate_go_back,
            }}
          />
        </div>
      )}

      <div
        ref={contentContainerRef}
        id="molecule-content"
        className="absolute top-1/2 left-3/4 z-[999] max-h-screen w-2xl max-w-[50vw] -translate-x-1/2 -translate-y-1/2 overflow-scroll rounded-sm p-4 text-white select-none lg:max-w-screen lg:overflow-visible [&_p]:pl-16 [&_p]:text-sm [&_p]:lg:text-base [&_ul]:mb-4 [&_ul]:pl-20"
        style={{ opacity: 0 }}
      >
        {/* Render title and content as HTML */}
        <div
          dangerouslySetInnerHTML={{
            __html: title + content + modalButtonHTML,
          }}
        />
        {/* Render Lottie animation if needed */}
        {showLottieGraph && (
          <div className="mt-4">
            <DotLottieReact
              src="/wp-content/themes/brut-communicatie-tw/graph.json"
              autoplay
            />
          </div>
        )}
      </div>

      {initSpline && (
        <div
          id="altuvoct-spline"
          className="h-screen [&_canvas]:absolute [&_canvas]:inset-0 [&_canvas]:opacity-0"
        >
          <img
            id="splineLoader"
            src="/wp-content/themes/brut-communicatie-tw/assets/img/loader.apng"
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
          />
          <Spline
            scene="https://prod.spline.design/5HBQ90-8clmYgbLL/scene.splinecode"
            onSplineMouseDown={handleClick}
            onLoad={initSplineModule}
          />
        </div>
      )}

      <div
        id="molecule-references"
        className="absolute bottom-0 z-[99999] h-[300px] w-full bg-primary p-8 pl-40 text-white"
      >
        <i
          className="fa-solid fa-xmark absolute top-6 left-6 cursor-pointer text-4xl"
          id="closeReferences"
          onClick={toggleReferencesPane}
        ></i>
        <h2>{brut_variables.translate_references}</h2>
        <ol class="list-decimal pl-4" ref={referencesContainerRef}>
          {references.map((ref, index) => (
            <li
              key={index}
              dangerouslySetInnerHTML={{ __html: ref.title }}
            ></li>
          ))}
        </ol>
      </div>
    </div>
  );
};

export default MoleculeComponent;
